/**
 * Utility to draw a ticket-shaped border using HTML5 Canvas
 * Features: rounded corners + circular notches on left/right edges
 */

export interface TicketShapeOptions {
  width: number;
  height: number;
  cornerRadius?: number;
  notchRadius?: number;
  notchPosition?: number; // 0-1, default 0.5 (center)
  borderColor?: string;
  backgroundColor?: string;
  borderWidth?: number;
}

/**
 * Draws a ticket shape on canvas with rounded corners and circular notches
 * The notches are circular cutouts on the left and right edges
 */
export const drawTicketShape = (
  ctx: CanvasRenderingContext2D,
  options: TicketShapeOptions
): void => {
  const {
    width,
    height,
    cornerRadius = 8,
    notchRadius = 8,
    notchPosition = 0.7,
    borderColor = 'grey',
    backgroundColor = 'white',
    borderWidth = 1,
  } = options;

  // Constants for notch angles (30 degrees = Math.PI / 6)
  const NOTCH_ANGLE = Math.PI / 9;

  // Calculate notch center position
  const notchY = height * notchPosition;

  // Clear canvas
  ctx.clearRect(0, 0, width, height);

  // Enable high-quality rendering
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  // Save current state
  ctx.save();

  // Create outer border path (for clipping)
  ctx.beginPath();
  ctx.moveTo(cornerRadius, 0);

  // Top edge
  ctx.lineTo(width - cornerRadius, 0);

  // Top-right corner
  ctx.quadraticCurveTo(width, 0, width, cornerRadius);

  // Right edge - top part (before notch)
  ctx.lineTo(width, notchY - notchRadius - notchRadius * Math.cos(NOTCH_ANGLE));

  const notchCenterXOffset = notchRadius * Math.sin(NOTCH_ANGLE);
  // Right notch - circular cutout (圆心在矩形外部)
  ctx.arc(width + notchCenterXOffset, notchY, notchRadius, (Math.PI * 3/ 2 - NOTCH_ANGLE), (NOTCH_ANGLE + Math.PI / 2), true);

  // Right edge - bottom part (after notch)
  ctx.lineTo(width, height - cornerRadius);

  // Bottom-right corner
  ctx.quadraticCurveTo(width, height, width - cornerRadius, height);

  // Bottom edge
  ctx.lineTo(cornerRadius, height);

  // Bottom-left corner
  ctx.quadraticCurveTo(0, height, 0, height - cornerRadius);

  // Left edge - bottom part (before notch)
  ctx.lineTo(0, notchY + notchRadius * Math.cos(NOTCH_ANGLE));

  // Left notch - circular cutout (圆心在矩形外部)
  ctx.arc(-notchCenterXOffset, notchY, notchRadius, Math.PI /2 - NOTCH_ANGLE, -(Math.PI / 2 - NOTCH_ANGLE), true);

  // Left edge - top part (after notch)
  ctx.lineTo(0, cornerRadius);

  // Top-left corner
  ctx.quadraticCurveTo(0, 0, cornerRadius, 0);

  ctx.closePath();

  // Set clipping region
  // Principle: Canvas stroke() draws half line width on each side of the path, making straight lines appear half width
  ctx.clip();

  // Fill background color
  ctx.fillStyle = backgroundColor;
  ctx.fill();

  // Draw border (using double line width, then clip)
  // Principle: Use double line width for drawing, then clip to ensure only inner part is visible, achieving correct line width
  ctx.restore();
  ctx.save();

  // Reset clipping region
  ctx.beginPath();
  ctx.moveTo(cornerRadius, 0);
  ctx.lineTo(width - cornerRadius, 0);
  ctx.quadraticCurveTo(width, 0, width, cornerRadius);
  ctx.lineTo(width, notchY - notchRadius - notchRadius * Math.cos(NOTCH_ANGLE));
  ctx.arc(width + notchCenterXOffset, notchY, notchRadius, (Math.PI * 3/ 2 - NOTCH_ANGLE), (NOTCH_ANGLE + Math.PI / 2), true);
  ctx.lineTo(width, height - cornerRadius);
  ctx.quadraticCurveTo(width, height, width - cornerRadius, height);
  ctx.lineTo(cornerRadius, height);
  ctx.quadraticCurveTo(0, height, 0, height - cornerRadius);
  ctx.lineTo(0, notchY + notchRadius * Math.cos(NOTCH_ANGLE));
  ctx.arc(-notchCenterXOffset, notchY, notchRadius, Math.PI /2 - NOTCH_ANGLE, -(Math.PI / 2 - NOTCH_ANGLE), true);
  ctx.lineTo(0, cornerRadius);
  ctx.quadraticCurveTo(0, 0, cornerRadius, 0);
  ctx.closePath();
  ctx.clip();

  // Draw border
  ctx.strokeStyle = borderColor;
  ctx.lineWidth = borderWidth * 2; // Double line width
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  ctx.stroke();

  // Restore state
  ctx.restore();
};

